import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IDashboardDateRange } from "../../../../@types/admin";
import { css } from "../../../../styles";

const { RangePicker } = DatePicker;

const rangePickerCss = css({
  // Match the purple used by the cohort select next to it instead of antd's
  // default blue, both on hover and while focused.
  "&:hover": {
    borderColor: "var(--colors-portal-text-input-border-hover) !important",
  },

  "&.ant-picker-focused": {
    borderColor: "var(--colors-portal-text-input-border-focus) !important",
    boxShadow: "0 0 0 2px rgba(189,180,221, 0.1) !important",
  },

  // The bar that underlines whichever date is being edited.
  ".ant-picker-active-bar": {
    background: "var(--colors-portal-text-input-border-focus) !important",
  },

  // Center each date inside its input.
  ".ant-picker-input > input": {
    textAlign: "center",
  },
});

interface IDateRangeFilterProps {
  value: IDashboardDateRange;
  onChange: (range: IDashboardDateRange) => void;
}

// EN-5191: a factory, not a module constant — a constant is evaluated at import time, before
// i18next is initialised, and every label would come back undefined.
const buildPresets = (
  t: TFunction
): { label: string; value: [Dayjs, Dayjs] }[] => [
  {
    label: t("reports_dateRange_last7"),
    value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("reports_dateRange_last30"),
    value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("reports_dateRange_last90"),
    value: [dayjs().subtract(90, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: t("reports_dateRange_thisMonth"),
    value: [dayjs().startOf("month"), dayjs().endOf("day")],
  },
  {
    label: t("reports_dateRange_lastMonth"),
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

export function DateRangeFilter({ value, onChange }: IDateRangeFilterProps) {
  const { t } = useTranslation();
  const presets = useMemo(() => buildPresets(t), [t]);
  return (
    <RangePicker
      className={rangePickerCss()}
      style={{ height: 40, width: 240, borderRadius: 20 }}
      allowClear={false}
      suffixIcon={null}
      format="MMM D, YYYY"
      value={[dayjs(value.startDate), dayjs(value.endDate)]}
      presets={presets}
      onChange={(dates) => {
        if (!dates || !dates[0] || !dates[1]) return;
        onChange({
          startDate: dates[0].startOf("day").toDate(),
          endDate: dates[1].endOf("day").toDate(),
        });
      }}
    />
  );
}
